/**
 * CPT/HCPCS code validation for care programs.
 * Rules:
 * - Exactly 5 characters
 * - No spaces, dashes, or special characters
 * - All digits (99213) OR 4 digits + 1 letter (0256T, 1234F)
 */

export const CPT_CODE_PATTERN = /^(?:\d{5}|\d{4}[A-Z])$/;

export function normalizeCptInput(value) {
  return String(value || '').replace(/[^a-zA-Z0-9,]/g, '').toUpperCase();
}

/** Strip invalid chars and cap each comma-separated segment at 5 characters. */
export function clampCptInput(value) {
  return normalizeCptInput(value)
    .split(',')
    .map((segment) => segment.slice(0, 5))
    .join(',');
}

export function normalizeCptCode(code) {
  return String(code || '').trim().toUpperCase();
}

export function isValidCptCode(code) {
  const normalized = normalizeCptCode(code);
  return normalized.length === 5 && CPT_CODE_PATTERN.test(normalized);
}

export function getCptCodeValidationError(code) {
  const raw = String(code ?? '');
  const normalized = normalizeCptCode(code);

  if (!normalized) {
    return 'CPT code is required.';
  }

  if (/\s/.test(raw) || /[-_./\\#@!$%^&*()+=[\]{}|;:'"<>?`~]/.test(raw)) {
    return 'CPT code cannot contain spaces, dashes, or special characters.';
  }

  if (/[^A-Za-z0-9]/.test(raw)) {
    return 'CPT code cannot contain spaces, dashes, or special characters.';
  }

  if (normalized.length !== 5) {
    return 'CPT code must be exactly 5 characters long.';
  }

  if (!CPT_CODE_PATTERN.test(normalized)) {
    return 'CPT code must be all numbers (e.g. 99213) or 4 numbers followed by 1 letter (e.g. 0256T).';
  }

  return null;
}

export function parseCptCodesFromInput(value) {
  return normalizeCptInput(value)
    .split(',')
    .map((c) => c.trim())
    .filter(Boolean);
}

export function validateCptCodeList(codes) {
  const list = Array.isArray(codes) ? codes : [];
  if (list.length === 0) {
    return 'At least one CPT code is required.';
  }

  for (const code of list) {
    const err = getCptCodeValidationError(code);
    if (err) {
      return `${normalizeCptCode(code) || code}: ${err}`;
    }
  }

  return null;
}

export function sanitizeCptCodeList(codes) {
  const list = Array.isArray(codes) ? codes : [];
  return [...new Set(list.map(normalizeCptCode).filter(isValidCptCode))];
}
